import mapboxgl from "mapbox-gl";
import {
  LEVEL_CONSTITUENCY,
  LEVEL_WARD,
  isTopLevel,
  levelIdKey,
  levelLabel,
  levelNameKey,
} from "../geoLevels";

/**
 * handleMapClick.js
 * ---------------------------------------------------------------------
 * Handles user clicks on map features in MapPage.
 *
 * ---------------------------------------------------------------------
 * 🧭 OVERVIEW
 *
 * 1️⃣ Determines which feature was clicked.
 * 2️⃣ Depending on current level and selection, handles:
 *     - A: Toggle off the selected feature (back up or reset)
 *     - B: Drill down from a top level (constituency or local authority) into
 *          the wards it contains
 *     - C: Select an individual ward
 * 3️⃣ Updates React state accordingly.
 * 4️⃣ Fetches metric and secondary data via provided functions.
 * 5️⃣ Zooms the map and refreshes the sidebar with updated aggregates.
 *
 * ---------------------------------------------------------------------
 * @param {Object} params
 * @param {Object} params.evt - Mapbox click event (with .features array)
 * @param {Object} params.state - Current MapPage state
 * @param {Object} params.mapRef - React ref to Mapbox map
 * @param {Function} params.setState - setState updater
 * @param {Function} params.fetchMetrics - Primary metrics fetcher
 * @param {Function} [params.fetchSecondaryMetrics] - Optional secondary metrics fetcher
 * @param {Function} params.loadGeo - Loads new geographic data
 * @param {Function} params.getAggregatesForSidebar - Returns data for sidebar
 * @param {Function} [params.onFeatureSelect] - Callback for sidebar update
 */

export async function handleMapClick({
  evt,
  state,
  mapRef,
  setState,
  fetchMetrics,
  fetchSecondaryMetrics,
  loadGeo,
  getAggregatesForSidebar,
  onFeatureSelect,
}) {
  const feature = evt.features?.[0];
  if (!feature) return;

  const map = mapRef.current?.getMap();
  const level = state.geoLevel;
  const p = feature.properties || {};
  const areaId = String(p.area_cd ?? "");

  // ------------------------------------------------------------
  // 🧩 Helper: safely compute feature bounds
  // ------------------------------------------------------------
  const safeBoundsFromFeatures = (features) => {
    if (!mapRef.current || !Array.isArray(features) || !features.length) return null;
    const bounds = new mapboxgl.LngLatBounds();
    const extend = (coords) => {
      if (!coords) return;
      if (typeof coords[0] === "number" && typeof coords[1] === "number")
        bounds.extend(coords);
      else if (Array.isArray(coords)) coords.forEach(extend);
    };
    features.forEach((f) => extend(f.geometry?.coordinates));
    return bounds;
  };

  // ------------------------------------------------------------
  // 🧩 Helper: zoom to a feature
  // ------------------------------------------------------------
  const fitToFeature = (f, padding = 28, duration = 500) => {
    const bounds = safeBoundsFromFeatures([f]);
    if (bounds) map?.fitBounds(bounds, { padding, duration });
    return bounds;
  };

  // ------------------------------------------------------------
  // 🧩 Helper: call both metric fetchers
  // ------------------------------------------------------------
  const callAllMetrics = async (lvl, codeOrNull) => {
    await fetchMetrics(lvl, codeOrNull);
    if (typeof fetchSecondaryMetrics === "function") {
      await fetchSecondaryMetrics(lvl, codeOrNull);
    }
  };

  // ------------------------------------------------------------
  // 🧩 Helper: refresh sidebar with new data
  // ------------------------------------------------------------
  const refreshSidebar = (feature, label, featureLevel, constituencyName = null) => {
    const data = getAggregatesForSidebar();
    onFeatureSelect?.({
      feature,
      data,
      selectedAreaLabel: label,
      selectedFeatureLevel: featureLevel,
      constName: constituencyName ?? label,
    });
  };

  // ------------------------------------------------------------
  // 🧩 CASE A: Clicked the currently selected feature
  // ------------------------------------------------------------
  const selected = state.selectedFeature;
  if (
    areaId &&
    selected &&
    selected.level === level &&
    selected.value === areaId
  ) {
// A1: If in ward view, clicking the current ward returns to the parent area's
// data context (but stays in ward geometry). The parent is whichever top level
// was drilled in from — a constituency or a local authority.
if (level === LEVEL_WARD && state.selectedConstituencyBounds) {
  const parentLevel = state.selectedParentLevel || LEVEL_CONSTITUENCY;
  const parentLabel =
    state.selectedConstituencyName ||
    p[levelNameKey(parentLevel)] ||
    `${levelLabel(parentLevel)} ${state.selectedConstituencyCode}`;

  map?.fitBounds(state.selectedConstituencyBounds, { padding: 28, duration: 500 });

  const parentFeature = {
    level: parentLevel,
    key: levelIdKey(parentLevel),
    value: state.selectedConstituencyCode,
    name: parentLabel,
    rowMatchIds: [state.selectedConstituencyCode],
  };

  setState({ selectedFeature: parentFeature }, () => {
    refreshSidebar(parentFeature, parentLabel, parentLevel, parentLabel);
  });

  return; // remain at ward map level, but parent-area data context
}

    // A2: At a top level there is nothing to step back up to, so clicking the
    // selected area again clears the selection and refits the country.
    if (isTopLevel(level)) {
      setState(
        {
          selectedFeature: null,
          selectedConstituencyCode: null,
          selectedConstituencyName: null,
          selectedConstituencyBounds: null,
          selectedParentLevel: null,
        },
        () => {
          map?.fitBounds([[-8.5, 49.8], [1.8, 60.9]], { padding: 20, duration: 500 });
          const data = getAggregatesForSidebar?.();
          onFeatureSelect?.({
            feature: null,
            data,
            selectedAreaLabel: null,
            selectedFeatureLevel: null,
            constName: null,
          });
        }
      );
      return;
    }
  }

  // ------------------------------------------------------------
  // 🧩 CASE B: Click a top-level area → drill down to its wards
  // ------------------------------------------------------------
  // Constituencies and local authorities behave identically here: select the
  // area, switch the map to ward geometry filtered to it, and keep the area
  // itself as the selected feature so the sidebar compares wards against it.
  if (isTopLevel(level)) {
    const parentCode = areaId || String(p[levelIdKey(level)] || "");
    if (!parentCode) return;

    const parentLabel =
      p[levelNameKey(level)] || p.NAME || `${levelLabel(level)} ${parentCode}`;
    const parentBounds = fitToFeature(feature);

    const parentFeature = {
      level,
      key: levelIdKey(level),
      value: parentCode,
      name: parentLabel,
      rowMatchIds: [parentCode],
    };

    setState(
      {
        selectedConstituencyCode: parentCode,
        selectedConstituencyName: parentLabel,
        selectedConstituencyBounds: parentBounds,
        selectedParentLevel: level,
        geoLevel: LEVEL_WARD,
        selectedFeature: parentFeature,
      },
      async () => {
        try {
          // Parent-area metrics first — the sidebar's comparison tier.
          await callAllMetrics(level, parentCode);

          // 🔹 Load ward geometry; wait for completion before next step
          await loadGeo(LEVEL_WARD, parentCode, { doFit: false });

          // Now fetch ward metrics only once
          await callAllMetrics(LEVEL_WARD, parentCode);

          // Finally refresh sidebar with correct info
          refreshSidebar(parentFeature, parentLabel, level, parentLabel);
        } catch (e) {
          console.error("[handleMapClick] Error fetching metrics:", e);
        }
      }
    );
    return;
  }


  // ------------------------------------------------------------
  // 🧩 CASE C: Click ward → select individual ward (MZ changed)
  if (level === LEVEL_WARD) {
    const wardCode = String(p.WD24CD || "");
    if (!wardCode) return;
  
    const wardLabel = p.WD24NM || p.NAME || `Ward ${wardCode}`;
    fitToFeature(feature);
  
    const newFeature = {
      level: "ward",
      key: "WD24CD",
      value: wardCode,
      name: wardLabel,
      rowMatchIds: [wardCode],
    };
  
    setState(
      { selectedFeature: newFeature },
      async () => {
        await callAllMetrics(LEVEL_WARD, state.selectedConstituencyCode);
        refreshSidebar(
          newFeature,                     
          wardLabel,
          LEVEL_WARD,
          state.selectedConstituencyName
        );
      }
    );
  }
}
